// Capture a DOM node exactly as rendered on screen and download as a PDF.
// Used by Financial Insights + Financial Health screens so the founder gets a
// pixel-perfect snapshot of the dashboard (full tiles, alerts, charts, etc.).

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

/**
 * @param {HTMLElement} node - DOM element to capture
 * @param {string} filename - desired PDF file name
 * @param {object} opts - { orientation?: 'portrait' | 'landscape' }
 */
export async function downloadNodeAsPdf(node, filename, opts = {}) {
  if (!node) {
    toast.error("Nothing to capture — page still loading?");
    return;
  }
  const orientation = opts.orientation || "portrait";
  try {
    toast.info("Rendering PDF — this can take 5-10s for big dashboards…");
    // Wait one frame so any lazy-loaded charts finish layout
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      windowWidth: node.scrollWidth,
      windowHeight: node.scrollHeight,
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    const pdf = new jsPDF({
      orientation,
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW - 10; // 5mm margin each side
    const imgH = (canvas.height * imgW) / canvas.width;

    if (imgH <= pageH - 10) {
      pdf.addImage(imgData, "JPEG", 5, 5, imgW, imgH);
    } else {
      // Multi-page: slice the canvas vertically
      const pageHeightPx = ((pageH - 10) * canvas.width) / imgW;
      let renderedHeight = 0;
      while (renderedHeight < canvas.height) {
        const sliceHeight = Math.min(pageHeightPx, canvas.height - renderedHeight);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeight;
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(
          canvas,
          0, renderedHeight, canvas.width, sliceHeight,
          0, 0, canvas.width, sliceHeight,
        );
        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
        const sliceH = (sliceHeight * imgW) / canvas.width;
        if (renderedHeight > 0) pdf.addPage();
        pdf.addImage(sliceData, "JPEG", 5, 5, imgW, sliceH);
        renderedHeight += sliceHeight;
      }
    }

    pdf.save(filename);
    toast.success("PDF downloaded");
  } catch (e) {
    console.error("PDF capture failed:", e);
    toast.error(`Could not generate PDF — ${e.message || "unknown error"}`);
  }
}
